import type { UseQueryResult } from '@tanstack/react-query';
import type { SearchResult } from '@/services/auth/types';
import type { ActiveCategory, SearchKindFilter } from '../constants';
import { DEFERRED_SOURCE_KEYS_LIST, SOURCE_KEYS, type SourceKey } from './source-descriptors';

export { SOURCE_KEYS, type SourceKey } from './source-descriptors';

/** Which data sources are active for the current search session. */
export type ActiveSources = Record<SourceKey, boolean>;

export const NO_SOURCES = Object.fromEntries(SOURCE_KEYS.map((key) => [key, false])) as ActiveSources;

const EAGER_WORKSPACE_SOURCE_KEYS = ['search', 'people', 'navigation'] as const satisfies ReadonlyArray<SourceKey>;

const DEFERRED_WORKSPACE_SOURCE_KEYS = DEFERRED_SOURCE_KEYS_LIST.filter((key) => key !== 'docs');

// Sources that fire only after the user scrolls to the sentinel in 'all' mode.
// In their dedicated filter modes (activeCategory === key) they fire immediately.
export const DEFERRED_SOURCE_KEYS = new Set<SourceKey>(DEFERRED_SOURCE_KEYS_LIST);

export const ORDERED_SOURCE_KEYS = [
  'docs',
  'navigation',
  'integrations',
  'adAudiences',
  'forms',
  'webhooks',
  'cdpOut',
  'dataOut',
  'objectTypes',
] as const satisfies ReadonlyArray<Exclude<SourceKey, 'search' | 'people'>>;

export type SourceQueryState = Pick<UseQueryResult<unknown>, 'isError' | 'isLoading' | 'isFetching' | 'dataUpdatedAt'>;

export type SearchSource<TResult extends SearchResult = SearchResult> = SourceQueryState & {
  results: TResult[];
};

type BuildSourceEnabledStatesOptions = {
  activeSources: ActiveSources;
  activeCategory: ActiveCategory;
  isSearchingEnabled: boolean;
  isDocsSearchingEnabled: boolean;
  deferredEnabled: boolean;
};

type BuildActiveSourcesOptions = {
  activeCategory: ActiveCategory;
  activeKindFilter: SearchKindFilter | null;
  canAccessForms: boolean;
  canAccessJourneyIntegrations: boolean;
  canAccessObjectTypes: boolean;
};

export function hasAnyDeferredSource(activeSources: ActiveSources): boolean {
  return [...DEFERRED_SOURCE_KEYS].some((key) => activeSources[key]);
}

export function buildActiveSources({
  activeCategory,
  activeKindFilter,
  canAccessForms,
  canAccessJourneyIntegrations,
  canAccessObjectTypes,
}: BuildActiveSourcesOptions): ActiveSources {
  // Navigation is handled locally — skip the API entirely to avoid stale Vertex results.
  if (activeCategory === 'navigation') return { ...NO_SOURCES, navigation: true };
  if (activeCategory === 'people') return { ...NO_SOURCES, people: true };
  if (activeCategory === 'docs') return { ...NO_SOURCES, docs: true };
  if (activeCategory === 'integrations') return { ...NO_SOURCES, integrations: true };
  if (activeCategory === 'forms') return { ...NO_SOURCES, forms: canAccessForms };
  if (activeCategory === 'objects') return { ...NO_SOURCES, objectTypes: canAccessObjectTypes };
  if (activeKindFilter != null) return { ...NO_SOURCES, search: true };

  return {
    search: true,
    people: true,
    docs: true,
    navigation: activeCategory === 'all',
    integrations: true,
    adAudiences: true,
    forms: canAccessForms,
    webhooks: canAccessJourneyIntegrations,
    cdpOut: canAccessJourneyIntegrations,
    dataOut: canAccessJourneyIntegrations,
    objectTypes: canAccessObjectTypes,
  };
}

export function buildSourceEnabledStates({
  activeSources,
  activeCategory,
  isSearchingEnabled,
  isDocsSearchingEnabled,
  deferredEnabled,
}: BuildSourceEnabledStatesOptions): ActiveSources {
  const deferredSourcesEnabled = activeCategory !== 'all' || deferredEnabled;
  const sourceEnabled = { ...NO_SOURCES };

  for (const key of EAGER_WORKSPACE_SOURCE_KEYS) {
    sourceEnabled[key] = activeSources[key] && isSearchingEnabled;
  }

  sourceEnabled.docs = activeSources.docs && isDocsSearchingEnabled && deferredSourcesEnabled;

  for (const key of DEFERRED_WORKSPACE_SOURCE_KEYS) {
    sourceEnabled[key] = activeSources[key] && isSearchingEnabled && deferredSourcesEnabled;
  }

  return sourceEnabled;
}

export function mergeQueryStates(states: readonly SourceQueryState[]): SourceQueryState {
  return {
    isError: states.some((state) => state.isError),
    isLoading: states.some((state) => state.isLoading),
    isFetching: states.some((state) => state.isFetching),
    dataUpdatedAt: Math.max(0, ...states.map((state) => state.dataUpdatedAt)),
  };
}
